import React, { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  marketingAdmin,
  marketingBlogPostUrl,
  marketingAdminAuthHeader,
  marketingRegenerateCoverUrl,
  marketingSyncGscStatsUrl,
  MARKETING_API_BASE,
} from '../../services/marketingAdminService';
import {
  runBlogBatchGeneration,
  stopBlogBatchGeneration,
  runBlogReformatBatch,
  stopBlogReformatBatch,
} from '../../services/blogGenerationService';
import { Plus, RefreshCw, ExternalLink, Sparkles, CheckSquare, Square, ChevronLeft, ChevronRight, Upload } from 'lucide-react';

const POSTS_PER_PAGE = 20;

const KEYWORDS_PER_PAGE = 25;

const FETCH_PAGE_SIZE = 1000;

async function fetchAllRows(buildQuery) {
  const all = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + FETCH_PAGE_SIZE - 1);
    if (error) throw error;
    const chunk = data || [];
    all.push(...chunk);
    if (chunk.length < FETCH_PAGE_SIZE) break;
    from += FETCH_PAGE_SIZE;
  }
  return all;
}

const statusClass = (status) => {
  if (status === 'published' || status === 'used') return 'bg-green-100 text-green-700';
  if (status === 'draft' || status === 'pending') return 'bg-yellow-100 text-yellow-700';
  if (status === 'archived' || status === 'skipped') return 'bg-gray-100 text-gray-600';
  return 'bg-gray-100 text-gray-600';
};

const queryErrorMessage = (err) => {
  if (!err) return '';
  return err.message || err.details || String(err);
};

const fmtNum = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '—';
  return v.toLocaleString('en-IN');
};

async function attachLiveViewCounts(posts) {
  const slugs = posts.map((p) => p.slug).filter(Boolean);
  if (slugs.length === 0) return posts;
  const counts = {};
  const headers = {
    'Content-Type': 'application/json',
    ...(await marketingAdminAuthHeader()),
  };
  for (let i = 0; i < slugs.length; i += 200) {
    const chunk = slugs.slice(i, i + 200);
    try {
      const res = await fetch(`${MARKETING_API_BASE}/blog/view-counts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ slugs: chunk }),
      });
      const json = await res.json().catch(() => ({}));
      Object.assign(counts, json.counts || {});
    } catch {
      /* keep supabase view_count if API is down */
    }
  }
  if (Object.keys(counts).length === 0) return posts;
  return posts.map((p) => ({
    ...p,
    view_count: Object.prototype.hasOwnProperty.call(counts, p.slug)
      ? counts[p.slug]
      : p.view_count,
  }));
}

const fmtPos = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '—';
  return v.toFixed(1);
};

const fmtCtr = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '—';
  return `${v.toFixed(1)}%`;
};

const rankClass = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return 'text-gray-400';
  if (v <= 10) return 'text-green-700 font-semibold';
  if (v <= 20) return 'text-amber-700 font-semibold';
  return 'text-gray-600';
};

const parseCsvLine = (line) => {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else inQuotes = false;
      } else cur += ch;
    } else if (ch === ',' || ch === '\t') {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim().replace(/^"|"$/g, ''));
};

const slugFromPageUrl = (page) => {
  try {
    const u = new URL(page, 'https://app.roadserve.in');
    const parts = u.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    const blogIdx = parts.indexOf('blog');
    if (blogIdx < 0 || !parts[blogIdx + 1] || parts[blogIdx + 1] === 'category') return null;
    return parts[blogIdx + 1];
  } catch {
    return null;
  }
};

const parseGscCsv = (text) => {
  const lines = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  let headerIdx = lines.findIndex((l) => {
    const cols = parseCsvLine(l).map((c) => c.toLowerCase());
    return cols.includes('page') && (cols.includes('clicks') || cols.includes('impressions'));
  });
  if (headerIdx < 0) headerIdx = lines.findIndex((l) => parseCsvLine(l)[0]?.startsWith('http'));
  if (headerIdx < 0) return [];
  const header = parseCsvLine(lines[headerIdx]).map((c) => c.toLowerCase());
  const hasHeader = header.includes('page');
  const start = hasHeader ? headerIdx + 1 : headerIdx;
  const idx = {
    page: Math.max(0, header.indexOf('page')),
    clicks: header.indexOf('clicks') >= 0 ? header.indexOf('clicks') : 1,
    impressions: header.indexOf('impressions') >= 0 ? header.indexOf('impressions') : 2,
    ctr: header.indexOf('ctr') >= 0 ? header.indexOf('ctr') : 3,
    position: header.indexOf('position') >= 0 ? header.indexOf('position') : 4,
  };
  const toNum = (v) => Number(String(v || '').replace(/%/g, '').replace(/,/g, '')) || 0;
  const rows = [];
  for (const line of lines.slice(start)) {
    const cols = parseCsvLine(line);
    const page = cols[idx.page] || '';
    if (!page.startsWith('http')) continue;
    const slug = slugFromPageUrl(page);
    if (!slug) continue;
    rows.push({
      slug,
      clicks: toNum(cols[idx.clicks]),
      impressions: toNum(cols[idx.impressions]),
      ctr: toNum(cols[idx.ctr]),
      position: toNum(cols[idx.position]),
    });
  }
  return rows;
};

const BLOG_CATEGORIES = [
  { slug: 'towing', name: 'Car Towing' },
  { slug: 'roadside-assistance', name: 'Roadside Assistance' },
  { slug: 'jump-start', name: 'Jump Start' },
  { slug: 'flat-tyre', name: 'Flat Tyre' },
  { slug: 'fuel-delivery', name: 'Fuel Delivery' },
  { slug: 'key-lockout', name: 'Key Lockout' },
  { slug: 'membership', name: 'RSA Membership' },
  { slug: 'car-care', name: 'Car Care' },
  { slug: 'tips-safety', name: 'Tips & Safety' },
];

const BlogAdmin = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('posts');
  const [keyword, setKeyword] = useState('');
  const [priority, setPriority] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const [reformatting, setReformatting] = useState(false);
  const [reformatProgress, setReformatProgress] = useState(null);
  const [coverLoadingSlug, setCoverLoadingSlug] = useState(null);
  const [generateMsg, setGenerateMsg] = useState('');
  const [selectedPostIds, setSelectedPostIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [postsPage, setPostsPage] = useState(1);
  const [keywordStatusFilter, setKeywordStatusFilter] = useState('all');
  const [keywordsPage, setKeywordsPage] = useState(1);
  const [postsSort, setPostsSort] = useState('clicks');
  const [postsSortDir, setPostsSortDir] = useState('desc');
  const [gscBusy, setGscBusy] = useState(false);
  const gscFileRef = useRef(null);

  const { data: posts = [], isLoading: postsLoading, isError: postsError, error: postsErr } = useQuery({
    queryKey: ['marketingBlogPosts', 'all-pages'],
    queryFn: async () => {
      try {
        const rows = await fetchAllRows(() =>
          marketingAdmin
            .from('blog_posts')
            .select(
              'id, title, slug, status, category, published_at, created_at, cover_image_url, view_count, gsc_clicks, gsc_impressions, gsc_ctr, gsc_position, gsc_synced_at',
            )
            .order('created_at', { ascending: false }),
        );
        return attachLiveViewCounts(rows);
      } catch (err) {
        if (!/gsc_|view_count|schema cache|column/i.test(err?.message || '')) throw err;
        try {
          const rows = await fetchAllRows(() =>
            marketingAdmin
              .from('blog_posts')
              .select(
                'id, title, slug, status, category, published_at, created_at, cover_image_url, gsc_clicks, gsc_impressions, gsc_ctr, gsc_position, gsc_synced_at',
              )
              .order('created_at', { ascending: false }),
          );
          return attachLiveViewCounts(rows);
        } catch (err2) {
          if (!/gsc_|schema cache|column/i.test(err2?.message || '')) throw err2;
          const rows = await fetchAllRows(() =>
            marketingAdmin
              .from('blog_posts')
              .select('id, title, slug, status, category, published_at, created_at, cover_image_url')
              .order('created_at', { ascending: false }),
          );
          return attachLiveViewCounts(rows);
        }
      }
    },
  });

  const { data: keywords = [], isLoading: kwLoading, isError: kwError, error: kwErr } = useQuery({
    queryKey: ['marketingBlogKeywords', 'all-pages'],
    queryFn: async () =>
      fetchAllRows(() =>
        marketingAdmin
          .from('keyword_queue')
          .select('*')
          .order('priority', { ascending: false })
          .order('created_at', { ascending: true }),
      ),
  });

  const { data: leads = [], isLoading: leadsLoading, isError: leadsError, error: leadsErr } = useQuery({
    queryKey: ['marketingWebsiteLeads'],
    queryFn: async () => {
      const { data, error } = await marketingAdmin
        .from('website_leads')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: jobs = [], isLoading: jobsLoading, isError: jobsError, error: jobsErr } = useQuery({
    queryKey: ['marketingContentJobs'],
    queryFn: async () => {
      const { data, error } = await marketingAdmin
        .from('content_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (postId) => {
      const { error } = await marketingAdmin
        .from('blog_posts')
        .update({ status: 'published' })
        .eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries(['marketingBlogPosts']),
  });

  const categoryMutation = useMutation({
    mutationFn: async ({ postId, category }) => {
      const { error } = await marketingAdmin
        .from('blog_posts')
        .update({ category })
        .eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries(['marketingBlogPosts']),
  });

  const bulkPublishMutation = useMutation({
    mutationFn: async (postIds) => {
      if (!postIds.length) return 0;
      const { error } = await marketingAdmin
        .from('blog_posts')
        .update({ status: 'published' })
        .in('id', postIds)
        .eq('status', 'draft');
      if (error) throw error;
      return postIds.length;
    },
    onSuccess: (count) => {
      setSelectedPostIds([]);
      queryClient.invalidateQueries(['marketingBlogPosts']);
      setGenerateMsg(`${count} draft(s) published successfully.`);
    },
  });

  const draftPosts = posts.filter((p) => p.status === 'draft');
  const pendingKeywordCount = keywords.filter((k) => k.status === 'pending').length;

  const statusCounts = useMemo(() => {
    const counts = { all: posts.length, draft: 0, published: 0, archived: 0 };
    for (const p of posts) {
      if (counts[p.status] !== undefined) counts[p.status] += 1;
    }
    return counts;
  }, [posts]);

  const keywordStatusCounts = useMemo(() => {
    const counts = { all: keywords.length, pending: 0, used: 0, skipped: 0 };
    for (const k of keywords) {
      if (counts[k.status] !== undefined) counts[k.status] += 1;
    }
    return counts;
  }, [keywords]);

  const filteredPosts = useMemo(() => {
    const list = statusFilter === 'all' ? posts : posts.filter((p) => p.status === statusFilter);
    const val = (p, key) => Number(p[key]) || 0;
    const dir = postsSortDir === 'asc' ? 1 : -1;
    const sorted = [...list];
    if (postsSort === 'clicks') {
      sorted.sort((a, b) => (val(a, 'gsc_clicks') - val(b, 'gsc_clicks')) * (postsSortDir === 'asc' ? 1 : -1));
    } else if (postsSort === 'views') {
      sorted.sort((a, b) => (val(a, 'view_count') - val(b, 'view_count')) * (postsSortDir === 'asc' ? 1 : -1));
    } else if (postsSort === 'impressions') {
      sorted.sort((a, b) => (val(a, 'gsc_impressions') - val(b, 'gsc_impressions')) * (postsSortDir === 'asc' ? 1 : -1));
    } else if (postsSort === 'ctr') {
      sorted.sort((a, b) => (val(a, 'gsc_ctr') - val(b, 'gsc_ctr')) * (postsSortDir === 'asc' ? 1 : -1));
    } else if (postsSort === 'position') {
      sorted.sort((a, b) => {
        const av = val(a, 'gsc_position');
        const bv = val(b, 'gsc_position');
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        return (av - bv) * dir;
      });
    } else {
      sorted.sort((a, b) => {
        const diff =
          new Date(a.published_at || a.created_at).getTime() -
          new Date(b.published_at || b.created_at).getTime();
        return diff * dir;
      });
    }
    return sorted;
  }, [posts, statusFilter, postsSort, postsSortDir]);

  const gscTotals = useMemo(() => {
    let clicks = 0;
    let impressions = 0;
    let views = 0;
    let posWeight = 0;
    let withTraffic = 0;
    let syncedAt = '';
    for (const p of posts) {
      const c = Number(p.gsc_clicks) || 0;
      const i = Number(p.gsc_impressions) || 0;
      const pos = Number(p.gsc_position) || 0;
      clicks += c;
      impressions += i;
      views += Number(p.view_count) || 0;
      if (i > 0) {
        withTraffic += 1;
        posWeight += pos * i;
      }
      if (p.gsc_synced_at && (!syncedAt || p.gsc_synced_at > syncedAt)) syncedAt = p.gsc_synced_at;
    }
    return {
      clicks,
      impressions,
      views,
      ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
      position: impressions > 0 ? posWeight / impressions : 0,
      withTraffic,
      syncedAt,
    };
  }, [posts]);

  const filteredKeywords = useMemo(() => {
    if (keywordStatusFilter === 'all') return keywords;
    return keywords.filter((k) => k.status === keywordStatusFilter);
  }, [keywords, keywordStatusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const safePage = Math.min(postsPage, totalPages);
  const pagedPosts = useMemo(() => {
    const start = (safePage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, safePage]);

  const keywordTotalPages = Math.max(1, Math.ceil(filteredKeywords.length / KEYWORDS_PER_PAGE));
  const safeKeywordPage = Math.min(keywordsPage, keywordTotalPages);
  const pagedKeywords = useMemo(() => {
    const start = (safeKeywordPage - 1) * KEYWORDS_PER_PAGE;
    return filteredKeywords.slice(start, start + KEYWORDS_PER_PAGE);
  }, [filteredKeywords, safeKeywordPage]);

  const setFilter = (next) => {
    setStatusFilter(next);
    setPostsPage(1);
    setSelectedPostIds([]);
  };

  const clickSort = (key) => {
    setPostsPage(1);
    if (postsSort === key) {
      setPostsSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setPostsSort(key);
    setPostsSortDir(key === 'position' ? 'asc' : 'desc');
  };

  const SortTh = ({ sortKey, children, align = 'right' }) => {
    const active = postsSort === sortKey;
    const arrow = !active ? '' : postsSortDir === 'asc' ? ' ↑' : ' ↓';
    return (
      <th className={`p-3 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
        <button
          type="button"
          onClick={() => clickSort(sortKey)}
          className={`inline-flex items-center gap-0.5 hover:text-gray-800 ${
            active ? 'text-primary-700 font-semibold' : 'text-gray-500'
          }`}
        >
          {children}
          {arrow}
        </button>
      </th>
    );
  };

  const setKeywordFilter = (next) => {
    setKeywordStatusFilter(next);
    setKeywordsPage(1);
  };

  const openPendingKeywords = () => {
    setTab('keywords');
    setKeywordStatusFilter('pending');
    setKeywordsPage(1);
  };

  const togglePostSelection = (postId) => {
    setSelectedPostIds((prev) =>
      prev.includes(postId) ? prev.filter((id) => id !== postId) : [...prev, postId],
    );
  };

  const visibleDraftIds = pagedPosts.filter((p) => p.status === 'draft').map((p) => p.id);
  const allVisibleDraftsSelected =
    visibleDraftIds.length > 0 && visibleDraftIds.every((id) => selectedPostIds.includes(id));

  const toggleSelectAllVisibleDrafts = () => {
    if (allVisibleDraftsSelected) {
      setSelectedPostIds((prev) => prev.filter((id) => !visibleDraftIds.includes(id)));
    } else {
      setSelectedPostIds((prev) => Array.from(new Set([...prev, ...visibleDraftIds])));
    }
  };

  const addKeywordMutation = useMutation({
    mutationFn: async ({ keyword: kw, priority: p }) => {
      const { error } = await marketingAdmin.from('keyword_queue').insert({
        keyword: kw,
        priority: p,
        status: 'pending',
        source: 'admin',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['marketingBlogKeywords']);
      setKeyword('');
      setPriority(0);
    },
  });

  const applyGscRows = async (rows) => {
    if (!rows.length) throw new Error('CSV mein Page / Clicks / Impressions columns nahi mile.');
    const bySlug = new Map();
    for (const row of rows) {
      const prev = bySlug.get(row.slug);
      if (!prev) {
        bySlug.set(row.slug, { ...row });
        continue;
      }
      const clicks = prev.clicks + row.clicks;
      const impressions = prev.impressions + row.impressions;
      bySlug.set(row.slug, {
        slug: row.slug,
        clicks,
        impressions,
        ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
        position:
          impressions > 0
            ? (prev.position * prev.impressions + row.position * row.impressions) / impressions
            : prev.position,
      });
    }
    const syncedAt = new Date().toISOString();
    const list = posts.filter((p) => bySlug.has(p.slug));
    for (let i = 0; i < list.length; i += 20) {
      const chunk = list.slice(i, i + 20);
      await Promise.all(
        chunk.map(async (post) => {
          const s = bySlug.get(post.slug);
          const { error } = await marketingAdmin
            .from('blog_posts')
            .update({
              gsc_clicks: Math.round(s.clicks),
              gsc_impressions: Math.round(s.impressions),
              gsc_ctr: Math.round(s.ctr * 100) / 100,
              gsc_position: Math.round(s.position * 10) / 10,
              gsc_synced_at: syncedAt,
            })
            .eq('id', post.id);
          if (error) throw error;
        }),
      );
    }
    return { matched: list.length, total: bySlug.size };
  };

  const handleGscCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setGscBusy(true);
    setGenerateMsg('Importing Search Console CSV…');
    try {
      const text = await file.text();
      const result = await applyGscRows(parseGscCsv(text));
      queryClient.invalidateQueries(['marketingBlogPosts']);
      setGenerateMsg(
        `GSC import: ${result.matched} blog(s) updated from ${result.total} page URL(s). Last 28 days clicks/rank ab table mein dikhenge.`,
      );
    } catch (err) {
      const msg = err.message || 'GSC import failed';
      if (/gsc_|column|schema cache/i.test(msg)) {
        setGenerateMsg(
          'Pehle Supabase SQL editor mein marketing-site/supabase/blog-gsc-stats.sql chalao, phir CSV import karo.',
        );
      } else {
        setGenerateMsg(msg);
      }
    } finally {
      setGscBusy(false);
    }
  };

  const handleSyncGsc = async () => {
    setGscBusy(true);
    setGenerateMsg('Syncing Google Search Console…');
    try {
      const res = await fetch(marketingSyncGscStatsUrl(), {
        method: 'POST',
        headers: { ...(await marketingAdminAuthHeader()), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || 'GSC sync failed');
      }
      queryClient.invalidateQueries(['marketingBlogPosts']);
      setGenerateMsg(
        `GSC sync: ${data.matched || 0} blog(s) updated (${data.startDate || '28d'}–${data.endDate || 'now'}).`,
      );
    } catch (err) {
      setGenerateMsg(
        `${err.message || 'GSC API sync nahi hua'}. Search Console → Performance → Pages → Export CSV, phir Import CSV use karo.`,
      );
    } finally {
      setGscBusy(false);
    }
  };

  const handleGenerateCover = async (slug) => {
    setCoverLoadingSlug(slug);
    setGenerateMsg('Generating cover image (~30 sec)…');
    try {
      const res = await fetch(marketingRegenerateCoverUrl(), {
        method: 'POST',
        headers: { ...(await marketingAdminAuthHeader()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Cover generation failed');
      }
      setGenerateMsg('Cover image added — refresh preview.');
      queryClient.invalidateQueries(['marketingBlogPosts']);
    } catch (err) {
      setGenerateMsg(err.message || 'Could not generate cover');
    } finally {
      setCoverLoadingSlug(null);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    setGenerateMsg('Stopping campaign after the current keyword finishes…');
    try {
      const result = await stopBlogBatchGeneration();
      if (result.data?.stopped) {
        setGenerateMsg(
          `Campaign stopped after ${result.data.completed || 0}/${result.data.total || 0}. Remaining keywords stay pending.`,
        );
      } else {
        setGenerateMsg(result.data?.message || 'No campaign was running.');
      }
      queryClient.invalidateQueries(['marketingBlogPosts']);
      queryClient.invalidateQueries(['marketingBlogKeywords']);
      queryClient.invalidateQueries(['marketingContentJobs']);
    } catch (err) {
      setGenerateMsg(err.message || 'Could not stop campaign');
    } finally {
      setStopping(false);
    }
  };

  const handleGenerate = async () => {
    if (pendingKeywordCount === 0) {
      setGenerateMsg('No pending keywords in queue. Add keywords first.');
      return;
    }

    setGenerating(true);
    setBatchProgress(null);
    setGenerateMsg(
      `Starting batch for ${pendingKeywordCount} keyword(s) — ~2 min each. Keep this tab open.`,
    );

    try {
      const result = await runBlogBatchGeneration((status) => {
        setBatchProgress(status);
        queryClient.invalidateQueries(['marketingBlogPosts']);
        queryClient.invalidateQueries(['marketingBlogKeywords']);
        if (status.running && status.total > 0) {
          const current = status.currentKeyword
            ? ` — now: "${status.currentKeyword}"`
            : '';
          setGenerateMsg(`Generating ${status.completed}/${status.total}${current}`);
        }
      });

      const lastError =
        (result.failed || 0) > 0 && Array.isArray(result.results)
          ? [...result.results].reverse().find((r) => !r.success && r.error)?.error
          : null;
      const errorSuffix = lastError ? ` Last error: ${String(lastError).slice(0, 200)}` : '';

      if (result.message && !result.started && (result.total === 0 || result.total === undefined)) {
        setGenerateMsg(result.message);
      } else if (result.stopped && !result.running) {
        setGenerateMsg(
          `Campaign stopped: ${result.succeeded || 0} draft(s) created, ${result.failed || 0} failed, ${result.completed || 0}/${result.total || 0} done. Remaining keywords stay pending.${errorSuffix}`,
        );
      } else {
        setGenerateMsg(
          `Batch complete: ${result.succeeded || 0} draft(s) created, ${result.failed || 0} failed. Review in Blog Posts tab.${errorSuffix}`,
        );
      }

      queryClient.invalidateQueries(['marketingBlogPosts']);
      queryClient.invalidateQueries(['marketingBlogKeywords']);
      queryClient.invalidateQueries(['marketingContentJobs']);
    } catch (err) {
      const msg = err.message || 'Could not generate blogs';
      if (msg.includes('Failed to fetch') || msg.includes('ECONNREFUSED')) {
        setGenerateMsg(
          process.env.NODE_ENV === 'development'
            ? 'Marketing API not reachable. In a new terminal run: cd marketing-site && npm run dev -- -p 3010'
            : 'Marketing API not reachable (app.roadserve.in/api/cron). Check CORS and that the marketing site is deployed.',
        );
      } else {
        setGenerateMsg(msg);
      }
    } finally {
      setGenerating(false);
      setBatchProgress(null);
    }
  };

  const handleStopReformat = async () => {
    setStopping(true);
    setGenerateMsg('Stopping reformat after the current post finishes…');
    try {
      const result = await stopBlogReformatBatch();
      if (result.data?.stopped) {
        setGenerateMsg(
          `Reformat stopped after ${result.data.completed || 0}/${result.data.total || 0}. Remaining posts stay in old format.`,
        );
      } else {
        setGenerateMsg(result.data?.message || 'No reformat batch was running.');
      }
      queryClient.invalidateQueries(['marketingBlogPosts']);
    } catch (err) {
      setGenerateMsg(err.message || 'Could not stop reformat');
    } finally {
      setStopping(false);
    }
  };

  const handleReformat = async () => {
    setReformatting(true);
    setReformatProgress(null);
    setGenerateMsg(
      'Reformatting old blogs into the new structure (quick answer, key takeaways, city links) — URLs stay the same. ~1 min per post. Keep this tab open.',
    );

    try {
      const result = await runBlogReformatBatch((status) => {
        setReformatProgress(status);
        queryClient.invalidateQueries(['marketingBlogPosts']);
        if (status.running && status.total > 0) {
          const current = status.currentSlug ? ` — now: "${status.currentSlug}"` : '';
          setGenerateMsg(`Reformatting ${status.completed}/${status.total}${current}`);
        }
      });

      const lastError =
        (result.failed || 0) > 0 && Array.isArray(result.results)
          ? [...result.results].reverse().find((r) => !r.success && r.error)?.error
          : null;
      const errorSuffix = lastError ? ` Last error: ${String(lastError).slice(0, 200)}` : '';

      if (result.message && !result.started && (result.total === 0 || result.total === undefined)) {
        setGenerateMsg(result.message);
      } else {
        setGenerateMsg(
          `Reformat done: ${result.succeeded || 0} post(s) updated, ${result.failed || 0} failed. URLs unchanged.${errorSuffix}`,
        );
      }

      queryClient.invalidateQueries(['marketingBlogPosts']);
    } catch (err) {
      setGenerateMsg(err.message || 'Could not reformat blogs');
    } finally {
      setReformatting(false);
      setReformatProgress(null);
    }
  };

  const tabs = [
    { id: 'posts', label: 'Blog Posts' },
    { id: 'keywords', label: 'Keywords' },
    { id: 'leads', label: 'Website Leads' },
    { id: 'jobs', label: 'AI Jobs' },
  ];

  const filterButtons = [
    { id: 'all', label: `All (${statusCounts.all})` },
    { id: 'draft', label: `Draft (${statusCounts.draft})` },
    { id: 'published', label: `Published (${statusCounts.published})` },
    { id: 'archived', label: `Archived (${statusCounts.archived})` },
  ];

  const keywordFilterButtons = [
    { id: 'all', label: `All (${keywordStatusCounts.all})` },
    { id: 'pending', label: `Pending (${keywordStatusCounts.pending})` },
    { id: 'used', label: `Used (${keywordStatusCounts.used})` },
    { id: 'skipped', label: `Skipped (${keywordStatusCounts.skipped})` },
  ];

  const loadErrors = [
    postsError && `Blog posts: ${queryErrorMessage(postsErr)}`,
    kwError && `Keywords: ${queryErrorMessage(kwErr)}`,
    leadsError && `Leads: ${queryErrorMessage(leadsErr)}`,
    jobsError && `AI jobs: ${queryErrorMessage(jobsErr)}`,
  ].filter(Boolean);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Site Admin</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage blog, keywords & leads for app.roadserve.in (admin login required)
          </p>
          {loadErrors.length > 0 && (
            <p className="mt-2 max-w-2xl text-sm text-red-700">
              Could not load marketing tables. Run{' '}
              <code className="rounded bg-red-50 px-1">supabase/fix-blog-admin-anon.sql</code> in
              the Supabase SQL editor, then refresh.{' '}
              {loadErrors.join(' · ')}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {generating && (
            <button
              type="button"
              onClick={handleStop}
              disabled={stopping}
              className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
            >
              <Square size={14} fill="currentColor" />
              {stopping ? 'Stopping…' : 'Stop campaign'}
            </button>
          )}
          {reformatting && (
            <button
              type="button"
              onClick={handleStopReformat}
              disabled={stopping}
              className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
            >
              <Square size={14} fill="currentColor" />
              {stopping ? 'Stopping…' : 'Stop reformat'}
            </button>
          )}
          <button
            type="button"
            onClick={handleReformat}
            disabled={reformatting || generating}
            title="Rewrite old published/draft blogs into the new format (quick answer, key takeaways, city hub links). URLs and titles stay the same."
            className="inline-flex items-center gap-2 rounded-lg border border-primary-300 bg-white px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-60"
          >
            {reformatting ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {reformatting
              ? reformatProgress?.total
                ? `Reformatting ${reformatProgress.completed}/${reformatProgress.total}…`
                : 'Starting reformat…'
              : 'Reformat Old Blogs'}
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || reformatting || pendingKeywordCount === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {generating ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating
              ? batchProgress?.total
                ? `Generating ${batchProgress.completed}/${batchProgress.total}…`
                : 'Starting batch…'
              : `Generate All Blogs (${pendingKeywordCount} pending)`}
          </button>
        </div>
      </div>

      {generateMsg && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          {generateMsg}
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'posts' && (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {filterButtons.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition ${
                  statusFilter === f.id
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              onClick={openPendingKeywords}
              className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
              title="Keywords waiting to generate"
            >
              Pending ({pendingKeywordCount})
            </button>
            <input
              ref={gscFileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleGscCsv}
            />
            <button
              type="button"
              onClick={() => gscFileRef.current?.click()}
              disabled={gscBusy}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Upload size={12} />
              Import GSC CSV
            </button>
            <button
              type="button"
              onClick={handleSyncGsc}
              disabled={gscBusy}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={12} className={gscBusy ? 'animate-spin' : ''} />
              Sync GSC
            </button>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Clicks (28d)</p>
              <p className="mt-1 text-xl font-bold text-gray-900">{fmtNum(gscTotals.clicks)}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Views</p>
              <p className="mt-1 text-xl font-bold text-gray-900">{fmtNum(gscTotals.views)}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Impressions</p>
              <p className="mt-1 text-xl font-bold text-gray-900">{fmtNum(gscTotals.impressions)}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Avg rank</p>
              <p className={`mt-1 text-xl font-bold ${rankClass(gscTotals.position)}`}>
                {fmtPos(gscTotals.position)}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">CTR · indexed</p>
              <p className="mt-1 text-xl font-bold text-gray-900">
                {fmtCtr(gscTotals.ctr)}
                <span className="ml-2 text-sm font-medium text-gray-400">{gscTotals.withTraffic} pages</span>
              </p>
            </div>
          </div>
          <p className="mb-3 text-xs text-gray-500">
            Google Search Console last 28 days
            {gscTotals.syncedAt
              ? ` · synced ${new Date(gscTotals.syncedAt).toLocaleString('en-IN')}`
              : ' · CSV import karo (Performance → Pages → Export) ya Sync GSC'}
            . Rank = average position.
          </p>

          {draftPosts.length > 0 && (statusFilter === 'all' || statusFilter === 'draft') && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
              <span className="text-sm text-gray-600">
                {draftPosts.length} draft(s)
                {selectedPostIds.length > 0 ? ` · ${selectedPostIds.length} selected` : ''}
              </span>
              <button
                type="button"
                onClick={() =>
                  bulkPublishMutation.mutate(
                    selectedPostIds.length ? selectedPostIds : draftPosts.map((p) => p.id),
                  )
                }
                disabled={bulkPublishMutation.isPending || draftPosts.length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
              >
                <CheckSquare size={16} />
                {bulkPublishMutation.isPending
                  ? 'Publishing…'
                  : selectedPostIds.length
                    ? `Publish selected (${selectedPostIds.length})`
                    : `Publish all drafts (${draftPosts.length})`}
              </button>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          {postsLoading ? (
            <p className="p-6 text-gray-500">Loading posts…</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-gray-50 text-gray-500">
                <tr>
                  <th className="p-3 w-10">
                    {visibleDraftIds.length > 0 && (
                      <input
                        type="checkbox"
                        checked={allVisibleDraftsSelected}
                        onChange={toggleSelectAllVisibleDrafts}
                        title="Select drafts on this page"
                        className="rounded border-gray-300"
                      />
                    )}
                  </th>
                  <th className="p-3 font-medium">Title</th>
                  <th className="p-3 font-medium">Category</th>
                  <th className="p-3 font-medium">Status</th>
                  <SortTh sortKey="clicks">Clicks</SortTh>
                  <SortTh sortKey="views">Views</SortTh>
                  <SortTh sortKey="impressions">Impr.</SortTh>
                  <SortTh sortKey="ctr">CTR</SortTh>
                  <SortTh sortKey="position">Rank</SortTh>
                  <SortTh sortKey="date" align="left">Date</SortTh>
                  <th className="p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pagedPosts.map((post) => (
                  <tr key={post.id}>
                    <td className="p-3">
                      {post.status === 'draft' ? (
                        <input
                          type="checkbox"
                          checked={selectedPostIds.includes(post.id)}
                          onChange={() => togglePostSelection(post.id)}
                          className="rounded border-gray-300"
                        />
                      ) : null}
                    </td>
                    <td className="p-3 font-medium text-gray-900 max-w-xs truncate">{post.title}</td>
                    <td className="p-3">
                      <select
                        value={post.category || 'tips-safety'}
                        onChange={(e) =>
                          categoryMutation.mutate({ postId: post.id, category: e.target.value })
                        }
                        disabled={categoryMutation.isPending}
                        className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-700 max-w-[140px]"
                      >
                        {BLOG_CATEGORIES.map((c) => (
                          <option key={c.slug} value={c.slug}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(post.status)}`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="p-3 text-right tabular-nums text-gray-800">{fmtNum(post.gsc_clicks)}</td>
                    <td className="p-3 text-right tabular-nums text-gray-800">{fmtNum(post.view_count)}</td>
                    <td className="p-3 text-right tabular-nums text-gray-800">{fmtNum(post.gsc_impressions)}</td>
                    <td className="p-3 text-right tabular-nums text-gray-500">{fmtCtr(post.gsc_ctr)}</td>
                    <td className={`p-3 text-right tabular-nums ${rankClass(post.gsc_position)}`}>
                      {fmtPos(post.gsc_position)}
                    </td>
                    <td className="p-3 text-gray-500">
                      {new Date(post.published_at || post.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td className="p-3 space-x-3">
                      <a
                        href={marketingBlogPostUrl(post.slug, post.status)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary-600 hover:underline"
                      >
                        View {post.status === 'draft' ? '(preview)' : ''} <ExternalLink size={14} />
                      </a>
                      {!post.cover_image_url && (
                        <button
                          type="button"
                          onClick={() => handleGenerateCover(post.slug)}
                          disabled={coverLoadingSlug === post.slug}
                          className="text-amber-700 font-medium hover:underline disabled:opacity-50"
                        >
                          {coverLoadingSlug === post.slug ? 'Cover…' : 'Add cover'}
                        </button>
                      )}
                      {post.status === 'draft' && (
                        <button
                          type="button"
                          onClick={() => publishMutation.mutate(post.id)}
                          className="text-green-700 font-medium hover:underline"
                        >
                          Publish
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!postsLoading && filteredPosts.length === 0 && (
            <p className="p-6 text-center text-gray-500">
              {posts.length === 0
                ? 'No posts yet. Add keywords and generate.'
                : `No ${statusFilter} posts.`}
            </p>
          )}
          {!postsLoading && filteredPosts.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
              <p className="text-xs text-gray-500">
                Showing {(safePage - 1) * POSTS_PER_PAGE + 1}–
                {Math.min(safePage * POSTS_PER_PAGE, filteredPosts.length)} of {filteredPosts.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPostsPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span className="text-xs font-medium text-gray-600">
                  Page {safePage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPostsPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {tab === 'keywords' && (
        <div>
          <form
            className="mb-4 flex flex-wrap gap-3 items-end bg-white p-4 rounded-xl border border-gray-200"
            onSubmit={(e) => {
              e.preventDefault();
              if (!keyword.trim()) return;
              addKeywordMutation.mutate({ keyword: keyword.trim(), priority: Number(priority) || 0 });
            }}
          >
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Keyword</label>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g. roadside assistance delhi"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="w-24">
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <input
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={addKeywordMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
            >
              <Plus size={16} /> Add
            </button>
            {addKeywordMutation.isError && (
              <p className="w-full text-sm text-red-600">{queryErrorMessage(addKeywordMutation.error)}</p>
            )}
          </form>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            {keywordFilterButtons.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setKeywordFilter(f.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition ${
                  keywordStatusFilter === f.id
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            {kwLoading ? (
              <p className="p-6 text-gray-500">Loading…</p>
            ) : (
              <>
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-gray-50 text-gray-500">
                    <tr>
                      <th className="p-3 font-medium">Keyword</th>
                      <th className="p-3 font-medium">Priority</th>
                      <th className="p-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pagedKeywords.map((kw) => (
                      <tr key={kw.id}>
                        <td className="p-3 text-gray-900">{kw.keyword}</td>
                        <td className="p-3 text-gray-500">{kw.priority}</td>
                        <td className="p-3">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(kw.status)}`}>
                            {kw.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredKeywords.length === 0 && (
                  <p className="p-6 text-center text-gray-500">
                    {kwError
                      ? queryErrorMessage(kwErr)
                      : keywords.length === 0
                      ? 'No keywords yet. If you already seeded the queue, run supabase/fix-blog-admin-anon.sql so this admin can read it.'
                      : `No ${keywordStatusFilter} keywords.`}
                  </p>
                )}
                {filteredKeywords.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
                    <p className="text-xs text-gray-500">
                      Showing {(safeKeywordPage - 1) * KEYWORDS_PER_PAGE + 1}–
                      {Math.min(safeKeywordPage * KEYWORDS_PER_PAGE, filteredKeywords.length)} of{' '}
                      {filteredKeywords.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setKeywordsPage((p) => Math.max(1, p - 1))}
                        disabled={safeKeywordPage <= 1}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        <ChevronLeft size={14} /> Prev
                      </button>
                      <span className="text-xs font-medium text-gray-600">
                        Page {safeKeywordPage} / {keywordTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setKeywordsPage((p) => Math.min(keywordTotalPages, p + 1))}
                        disabled={safeKeywordPage >= keywordTotalPages}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        Next <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'leads' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          {leadsLoading ? (
            <p className="p-6 text-gray-500">Loading…</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-gray-50 text-gray-500">
                <tr>
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Phone</th>
                  <th className="p-3 font-medium">Source</th>
                  <th className="p-3 font-medium">City</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {leads.map((lead) => (
                  <tr key={lead.id}>
                    <td className="p-3 text-gray-900">{lead.name || '—'}</td>
                    <td className="p-3 font-medium">{lead.phone}</td>
                    <td className="p-3 text-gray-500">{lead.source}</td>
                    <td className="p-3 text-gray-500">{lead.city || '—'}</td>
                    <td className="p-3">{lead.status}</td>
                    <td className="p-3 text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          {jobsLoading ? (
            <p className="p-6 text-gray-500">Loading…</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-gray-50 text-gray-500">
                <tr>
                  <th className="p-3 font-medium">Keyword</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium">Result</th>
                  <th className="p-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td className="p-3 text-gray-900">{job.keyword}</td>
                    <td className="p-3">{job.status}</td>
                    <td className="p-3 text-gray-500">{job.result_slug || job.error || '—'}</td>
                    <td className="p-3 text-gray-500">
                      {new Date(job.created_at).toLocaleDateString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;
